/**
 * useTemplateEditor Hook
 *
 * Manages the state and operations for editing a single template.
 * Works with the V2 denormalized template system where sections
 * have inline content (no module resolution needed).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEntitlementStatus } from '@features/entitlements';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useForkTemplateMutation,
  useUpdateSectionMutation,
  useUpdateTemplateMutation,
} from '../store/api';
import type { SectionWithMeta, TemplateWithMeta } from '../types';

interface UseTemplateEditorProps {
  /** Template to edit */
  template: TemplateWithMeta | null;
  /** Callback when template is saved */
  onSave?: () => void;
  /** Callback when a copy is created (transparent copy-on-write) */
  onTemplateForked?: (newTemplateId: string) => void;
}

interface UseTemplateEditorReturn {
  /** Template name (editable) */
  templateName: string;
  /** Template description */
  templateDescription: string | null;
  /** Template sections with metadata */
  sections: SectionWithMeta[];
  /** Whether template has unsaved changes */
  hasChanges: boolean;
  /** Whether title is being edited */
  isTitleEditing: boolean;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Whether user can edit this template */
  canEdit: boolean;
  /** Whether user can copy this template */
  canFork: boolean;
  /** Start editing title */
  startEditingTitle: () => void;
  /** Update title */
  updateTitle: (title: string) => void;
  /** Finish editing title (saves automatically) */
  finishEditingTitle: () => Promise<void>;
  /** Update description */
  updateDescription: (description: string) => void;
  /** Add section at index */
  addSection: (section: Omit<SectionWithMeta, 'showPreview'>, index?: number) => void;
  /** Remove section */
  removeSection: (sectionId: string) => void;
  /** Reorder sections */
  reorderSections: (startIndex: number, endIndex: number) => void;
  /** Update section content */
  updateSectionContent: (sectionId: string, content: string) => Promise<boolean>;
  /** Update section title */
  updateSectionTitle: (sectionId: string, title: string) => void;
  /** Save all changes */
  save: () => Promise<boolean>;
  /** Reset to original state */
  reset: () => void;
}

export const useTemplateEditor = ({
  template,
  onSave,
  onTemplateForked,
}: UseTemplateEditorProps): UseTemplateEditorReturn => {
  const { template: templateMetrics } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  // Mutations
  const [updateTemplateMutation, { isLoading: isUpdating }] = useUpdateTemplateMutation();
  const [updateSectionMutation, { isLoading: isUpdatingSection }] = useUpdateSectionMutation();
  const [forkMutation, { isLoading: isForking }] = useForkTemplateMutation();

  // State
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState<string | null>(null);
  const [sections, setSections] = useState<SectionWithMeta[]>([]);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Track original state for change detection
  const originalStateRef = useRef<{
    name: string;
    description: string | null;
    sections: SectionWithMeta[];
  } | null>(null);
  const hasTrackedStart = useRef(false);

  // Pending autosave flag
  const pendingAutosaveRef = useRef(false);

  // Track if we've already forked during this session
  const forkedTemplateIdRef = useRef<string | null>(null);

  // Get entitlement status for template creation
  const { isBlocked: isTemplateBlocked, isFeatureEnabled: isEntitlementEnabled } =
    useEntitlementStatus('template_creation');

  // Permissions from template
  const canEdit = template?.permissions.can_edit ?? false;
  // Override can_fork when entitlement is blocked (and feature is enabled)
  const canFork =
    (template?.permissions.can_fork ?? false) && !(isEntitlementEnabled && isTemplateBlocked);

  // Transform template sections to SectionWithMeta
  const transformSections = useCallback(
    (templateSections: TemplateWithMeta['revision']['sections']): SectionWithMeta[] => {
      if (!templateSections) return [];
      return templateSections.map((section, index) => ({
        id: section.id,
        title: section.title,
        order: section.order ?? index,
        content: section.content,
        legacyModuleId: section.legacy_module_id,
        exampleOutput: section.example_output ?? null,
        showPreview: false,
      }));
    },
    [],
  );

  // Transparent copy-on-write: check if we need to create a copy before editing
  const needsCopyOnWrite = !canEdit && canFork;

  /**
   * Helper to ensure the user has their own editable copy of the template.
   * If they are editing a read-only template that allows copying, this will
   * trigger the copy (fork) operation.
   */
  const ensureCopyIfNeeded = useCallback(async (): Promise<string | null> => {
    // Already editable or no template selected
    if (!needsCopyOnWrite || !template) {
      return template?.template.id || null;
    }

    // Already forked in this session
    if (forkedTemplateIdRef.current) {
      return forkedTemplateIdRef.current;
    }

    try {
      const result = await forkMutation({ templateId: template.template.id, forkNoteTemplateRequest: {} }).unwrap();
      const newTemplateId = result.template.id;
      forkedTemplateIdRef.current = newTemplateId;

      // Update local state with the new template's data immediately
      // This ensures that subsequent section updates use the new IDs
      const transformedSections = transformSections(result.revision.sections);
      setSections(transformedSections);

      // Also update original state to match the new template
      if (originalStateRef.current) {
        originalStateRef.current = {
          name: result.revision.title,
          description: result.revision.description ?? null,
          sections: transformedSections,
        };
      }

      templateMetrics.customizationStarted(newTemplateId, {
        source: 'public_copy',
        initial_sections_count: result.revision.sections?.length || 0,
      });

      enqueueSnackbar('Template copied to your templates', { variant: 'success' });
      onTemplateForked?.(newTemplateId);
      return newTemplateId;
    } catch (error) {
      console.error('Failed to copy template:', error);
      enqueueSnackbar('Failed to copy template', { variant: 'error' });
      return null;
    }
  }, [
    needsCopyOnWrite,
    template,
    forkMutation,
    transformSections,
    templateMetrics,
    enqueueSnackbar,
    onTemplateForked,
  ]);

  // Calculate if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!originalStateRef.current) return false;

    const nameChanged = templateName !== originalStateRef.current.name;
    const descriptionChanged = templateDescription !== originalStateRef.current.description;
    const sectionsChanged =
      JSON.stringify(sections) !== JSON.stringify(originalStateRef.current.sections);

    return nameChanged || descriptionChanged || sectionsChanged;
  }, [templateName, templateDescription, sections]);

  // Initialize state from template
  useEffect(() => {
    if (template) {
      const transformedSections = transformSections(template.revision.sections);
      setTemplateName(template.revision.title);
      setTemplateDescription(template.revision.description ?? null);
      setSections(transformedSections);
      originalStateRef.current = {
        name: template.revision.title,
        description: template.revision.description ?? null,
        sections: transformedSections,
      };
      setLastSaved(null);
      hasTrackedStart.current = false;
    }
  }, [template, transformSections]);

  // Track customization start
  useEffect(() => {
    if (!hasTrackedStart.current && template && canEdit) {
      templateMetrics.customizationStarted(template.template.id, {
        source: 'existing',
        initial_sections_count: template.revision.sections?.length || 0,
      });
      hasTrackedStart.current = true;
    }
  }, [template, canEdit, templateMetrics]);

  // Start editing title
  const startEditingTitle = useCallback(() => {
    if (canEdit || canFork) {
      setIsTitleEditing(true);
    }
  }, [canEdit, canFork]);

  // Update title
  const updateTitle = useCallback((title: string) => {
    setTemplateName(title);
  }, []);

  // Finish editing title - auto-save if changed
  const finishEditingTitle = useCallback(async () => {
    setIsTitleEditing(false);

    if (!template || !(canEdit || canFork)) return;

    const trimmedName = templateName.trim();
    if (trimmedName !== originalStateRef.current?.name) {
      // Ensure we have an editable copy before saving
      const targetTemplateId = await ensureCopyIfNeeded();
      if (!targetTemplateId) return;

      templateMetrics.sectionModified(targetTemplateId, {
        action: 'edit_title',
        section_type: 'template_title',
      });

      try {
        await updateTemplateMutation({
          templateId: targetTemplateId,
          updateNoteTemplateRequest: {
            title: trimmedName,
          },
        }).unwrap();

        if (originalStateRef.current) {
          originalStateRef.current.name = trimmedName;
        }
        setLastSaved(new Date());
        onSave?.();
      } catch (error) {
        console.error('Failed to save template title:', error);
        enqueueSnackbar('Failed to save template title', { variant: 'error' });
      }
    }
  }, [
    template,
    canEdit,
    canFork,
    templateName,
    ensureCopyIfNeeded,
    templateMetrics,
    updateTemplateMutation,
    onSave,
    enqueueSnackbar,
  ]);

  // Update description
  const updateDescription = useCallback((description: string) => {
    setTemplateDescription(description);
    pendingAutosaveRef.current = true;
  }, []);

  // Add section
  const addSection = useCallback(
    (section: Omit<SectionWithMeta, 'showPreview'>, index?: number) => {
      if (!(canEdit || canFork)) return;

      const newSection: SectionWithMeta = {
        ...section,
        showPreview: false,
      };

      setSections(prev => {
        const updated = [...prev];
        if (index !== undefined && index !== null) {
          updated.splice(index, 0, newSection);
        } else {
          updated.push(newSection);
        }
        // Update order values
        return updated.map((s, i) => ({ ...s, order: i }));
      });

      if (template) {
        templateMetrics.addSection(forkedTemplateIdRef.current || template.template.id, {
          section_type: 'custom',
        });
      }

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork, template, templateMetrics],
  );

  // Remove section
  const removeSection = useCallback(
    (sectionId: string) => {
      if (!(canEdit || canFork)) return;

      setSections(prev => {
        const updated = prev.filter(s => s.id !== sectionId);
        // Update order values
        return updated.map((s, i) => ({ ...s, order: i }));
      });

      if (template) {
        templateMetrics.removeSection(forkedTemplateIdRef.current || template.template.id, {
          section_type: 'custom',
        });
      }

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork, template, templateMetrics],
  );

  // Reorder sections
  const reorderSections = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!(canEdit || canFork) || startIndex === endIndex) return;

      setSections(prev => {
        const result = [...prev];
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        // Update order values
        return result.map((s, i) => ({ ...s, order: i }));
      });

      if (template) {
        templateMetrics.sectionModified(forkedTemplateIdRef.current || template.template.id, {
          action: 'reorder',
          section_position: endIndex,
        });
      }

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork, template, templateMetrics],
  );

  // Update section content (uses dedicated endpoint)
  const updateSectionContent = useCallback(
    async (sectionId: string, content: string): Promise<boolean> => {
      if (!template || !(canEdit || canFork)) return false;

      // Ensure we have an editable copy before saving
      const targetTemplateId = await ensureCopyIfNeeded();
      if (!targetTemplateId) return false;

      // Find the correct section ID if we just forked
      // If forkedTemplateIdRef.current is set, it means we just forked.
      // However, the sectionId passed in might be the OLD ID from the UI.
      // But ensureCopyIfNeeded updates the `sections` state, so if the UI
      // re-renders, it will have the new IDs.
      // If it hasn't re-rendered yet, we need to map the old ID to the new one.

      let targetSectionId = sectionId;
      if (forkedTemplateIdRef.current) {
        // Find the section by index/order if the ID doesn't match
        const oldSectionIndex = sections.findIndex(s => s.id === sectionId);
        if (oldSectionIndex !== -1) {
          // The `sections` state was already updated in ensureCopyIfNeeded
          targetSectionId = sections[oldSectionIndex].id;
        }
      }

      try {
        await updateSectionMutation({
          templateId: targetTemplateId,
          sectionId: targetSectionId,
          updateSectionContentRequest: { content },
        }).unwrap();

        // Update local state
        setSections(prev => prev.map(s => (s.id === targetSectionId ? { ...s, content } : s)));

        setLastSaved(new Date());
        return true;
      } catch (error) {
        console.error('Failed to update section content:', error);
        enqueueSnackbar('Failed to save section', { variant: 'error' });
        return false;
      }
    },
    [
      template,
      canEdit,
      canFork,
      ensureCopyIfNeeded,
      sections,
      updateSectionMutation,
      enqueueSnackbar,
    ],
  );

  // Update section title (local only, saved with full save)
  const updateSectionTitle = useCallback(
    (sectionId: string, title: string) => {
      if (!(canEdit || canFork)) return;

      setSections(prev => prev.map(s => (s.id === sectionId ? { ...s, title } : s)));

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork],
  );

  // Save all changes
  const save = useCallback(async (): Promise<boolean> => {
    if (!template || !(canEdit || canFork)) return false;

    // Ensure we have an editable copy before saving
    const targetTemplateId = await ensureCopyIfNeeded();
    if (!targetTemplateId) return false;

    try {
      await updateTemplateMutation({
        templateId: targetTemplateId,
        updateNoteTemplateRequest: {
          title: templateName,
          description: templateDescription,
          sections: sections.map(s => ({
            id: s.id,
            title: s.title,
            order: s.order,
            content: s.content,
            example_output: s.exampleOutput,
          })),
        },
      }).unwrap();

      setLastSaved(new Date());
      originalStateRef.current = {
        name: templateName,
        description: templateDescription,
        sections: [...sections],
      };
      pendingAutosaveRef.current = false;
      enqueueSnackbar('Template saved successfully', { variant: 'success' });
      onSave?.();
      return true;
    } catch (error) {
      console.error('Failed to save template:', error);
      enqueueSnackbar('Failed to save template', { variant: 'error' });
      return false;
    }
  }, [
    template,
    canEdit,
    canFork,
    ensureCopyIfNeeded,
    templateName,
    templateDescription,
    sections,
    updateTemplateMutation,
    onSave,
    enqueueSnackbar,
  ]);

  // Autosave when pending changes exist
  useEffect(() => {
    if (!pendingAutosaveRef.current || !template || !(canEdit || canFork)) {
      return;
    }

    pendingAutosaveRef.current = false;

    // Perform silent autosave
    void (async () => {
      // Ensure we have an editable copy before saving
      const targetTemplateId = await ensureCopyIfNeeded();
      if (!targetTemplateId) return;

      try {
        await updateTemplateMutation({
          templateId: targetTemplateId,
          updateNoteTemplateRequest: {
            title: templateName,
            description: templateDescription,
            sections: sections.map(s => ({
              id: s.id,
              title: s.title,
              order: s.order,
              content: s.content,
              example_output: s.exampleOutput,
            })),
          },
        }).unwrap();

        setLastSaved(new Date());
        originalStateRef.current = {
          name: templateName,
          description: templateDescription,
          sections: [...sections],
        };
      } catch (error) {
        console.error('Autosave failed:', error);
      }
    })();
  }, [
    sections,
    template,
    canEdit,
    canFork,
    ensureCopyIfNeeded,
    templateName,
    templateDescription,
    updateTemplateMutation,
  ]);

  // Reset to original state
  const reset = useCallback(() => {
    if (originalStateRef.current) {
      setTemplateName(originalStateRef.current.name);
      setTemplateDescription(originalStateRef.current.description);
      setSections([...originalStateRef.current.sections]);
    }
  }, []);

  return {
    templateName,
    templateDescription,
    sections,
    hasChanges,
    isTitleEditing,
    isSaving: isUpdating || isUpdatingSection || isForking,
    lastSaved,
    canEdit,
    canFork,
    startEditingTitle,
    updateTitle,
    finishEditingTitle,
    updateDescription,
    addSection,
    removeSection,
    reorderSections,
    updateSectionContent,
    updateSectionTitle,
    save,
    reset,
  };
};
